import { Button, HStack, Text, useBreakpointValue, type ResponsiveValue } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useContext } from 'react';
import type { League } from '@/models';
import type { leagueMember } from '@/models/user';
import { sentinelType } from '@/models/gameUpdate';
import {
  getJoinOrderRank,
  getListPosition,
  isPendingLeagueRank,
  isSettledLeagueRank,
  memberHasList,
} from '@/helpers/leagues/leagueMemberSync';
import { prefetchLeagueLeaderboard } from '@/helpers/leagues/leagueLeaderboardCache';
import { AdminTag, LatecomerTag, RankChangeIndicator } from '../../components';
import { FirebaseContext } from '@/session/FirebaseContext';
import { leagueRowButtonProps, singleLineEllipsisTextProps } from '@/components/singleLineEllipsis';
import { ordinalSuffix } from '../../helpers';
import { useGameSession } from '@/session/useGameSession';

interface LeagueButtonProps {
  leagueID: string;
  league: League;
  memberData: leagueMember;
}

export function LeagueButton(props: LeagueButtonProps) {
  const { gameUpdates } = useGameSession();
  const tournamentStarted = Object.keys(gameUpdates).includes(sentinelType.tournamentStarted);
  const ctx = useContext(FirebaseContext);
  const { leagueID, league, memberData } = props;
  const hasList = memberHasList(memberData);
  const rankToDisplay = isSettledLeagueRank(memberData.currentRank)
    ? memberData.currentRank
    : getJoinOrderRank(league, memberData, ctx.user!.uid);
  const leagueButtonTextSize = useBreakpointValue<ResponsiveValue<number>>({
    base: 12,
    lg: 13,
    xl: 13,
  });

  function warmLeaderboardCache() {
    void prefetchLeagueLeaderboard(ctx.db, { ...league, id: leagueID });
  }

  return (
    <Link to={`../league/${leagueID}`} style={{ display: 'block', width: '100%', minWidth: 0 }}>
      <Button
        {...leagueRowButtonProps}
        onMouseEnter={warmLeaderboardCache}
        onTouchStart={warmLeaderboardCache}
      >
        <HStack width='full' spacing={4} align='center' minW={0} flex={1}>
          <Text
            {...singleLineEllipsisTextProps}
            flex={1}
            textAlign='start'
            fontSize={leagueButtonTextSize}
          >
            {league.name}
          </Text>
          <HStack spacing={2} flexShrink={0}>
            {league.adminUID === ctx.user!.uid ? (
              <AdminTag fontSize={leagueButtonTextSize} compact />
            ) : null}
            {memberData.latecomer ? (
              <LatecomerTag fontSize={leagueButtonTextSize} compact />
            ) : null}
            {!hasList ? (
              <Text fontWeight='bold' fontSize={leagueButtonTextSize} flexShrink={0}>
                -----
              </Text>
            ) : isPendingLeagueRank(memberData.currentRank) ? (
              <Text fontWeight='bold' fontStyle='italic' fontSize={leagueButtonTextSize} flexShrink={0}>
                pending
              </Text>
            ) : (
              <>
                <Text fontWeight='bold' fontSize={leagueButtonTextSize} flexShrink={0}>
                  {!tournamentStarted ? (
                    '-----'
                  ) : (
                    <>
                      {rankToDisplay}
                      <sup>{ordinalSuffix(rankToDisplay)}</sup>
                    </>
                  )}
                </Text>
                {isSettledLeagueRank(memberData.currentRank) && tournamentStarted ? (
                  <RankChangeIndicator
                    memberData={memberData}
                    fontSize={leagueButtonTextSize}
                    getListPosition={() => getListPosition(league, memberData, ctx.user!.uid)}
                  />
                ) : null}
              </>
            )}
          </HStack>
        </HStack>
      </Button>
    </Link>
  );
}
